import { AssetClassCode } from "@/types/assets";

export const supportedLocales = ['pt', 'en'] as const;

export type Locale = (typeof supportedLocales)[number];

export const translations = {
  pt: {
    common: {
      currencySymbol: 'R$',
    },
    header: {
      accountFallback: 'Conta',
      emailFallback: 'E-mail n\u00e3o informado',
      profileLabel: 'Perfil do usu\u00e1rio',
      settingsLabel: 'Configura\u00e7\u00f5es',
      signOutLabel: 'Sair',
    },
    sidebar: {
      navAriaLabel: 'Navega\u00e7\u00e3o principal',
      dashboard: 'Dashboard',
      contributions: 'Aportes',
      assetClasses: 'Carteira meta',
      targetPortfolio: 'Ativos',
      market: 'Mercado',
      proventos: 'Proventos',
      settings: 'Configura\u00e7\u00f5es',
      unlock: 'Ocultar barra lateral',
      lock: 'Abrir barra lateral',
      lockedTitle: 'Barra lateral aberta',
      floatingTitle: 'Barra lateral fechada',
    },
    authGuard: {
      validating: 'Validando sess\u00e3o...',
    },
    allocationCard: {
      title: 'Aloca\u00e7\u00e3o atual',
      subtitle: 'Indicador com meta marcada',
    },
    allocationProgress: {
      above: 'Acima da meta',
      below: 'Abaixo da meta',
    },
    totalAssetsCard: {
      title: 'Patrim\u00f4nio total',
    },
    login: {
      validations: {
        emailRequired: 'Informe um e-mail v\u00e1lido',
        emailInvalid: 'Formato de e-mail inv\u00e1lido',
        passwordRequired: 'Informe sua senha',
        passwordMin: 'A senha deve ter no m\u00ednimo 8 caracteres',
      },
      highlights: [
        {
          title: 'Rebalanceamento com contexto',
          description: 'Priorize aportes e vendas com base em metas configur\u00e1veis.',
        },
        {
          title: 'Alertas inteligentes',
          description: 'Receba notifica\u00e7\u00f5es sempre que um ativo sair da banda ideal.',
        },
        {
          title: 'Insights acion\u00e1veis',
          description: 'Sugest\u00f5es claras do pr\u00f3ximo passo para manter o plano em dia.',
        },
      ],
      heroTitle: 'Controle total da sua carteira',
      heroEmphasis: 'em um s\u00f3 lugar',
      subtitle:
        'Centralize aportes, acompanhe metas e execute rebalanceamentos com recomenda\u00e7\u00f5es feitas para voc\u00ea.',
      panelAriaLabel: 'Acesso ao sistema',
      formTitle: 'Entre na sua conta',
      formSubtitle: 'Utilize seu e-mail cadastrado para acessar a plataforma.',
      emailLabel: 'E-mail',
      emailPlaceholder: 'voce@email.com',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Digite sua senha',
      submitLabel: 'Entrar',
      submitLoading: 'Entrando...',
      switchPrompt: 'Ainda n\u00e3o possui conta?',
      switchAction: 'Crie agora',
    },
    signup: {
      validations: {
        fullNameRequired: 'Informe seu nome completo',
        emailRequired: 'Informe um e-mail v\u00e1lido',
        emailInvalid: 'Formato de e-mail inv\u00e1lido',
        passwordRequired: 'Crie uma senha',
        passwordMin: 'A senha deve ter no m\u00ednimo 8 caracteres',
        confirmPasswordRequired: 'Confirme sua senha',
        confirmPasswordMatch: 'As senhas precisam ser iguais',
      },
      highlights: [
        {
          title: 'Planejamento guiado',
          description: 'Monte sua carteira com sugest\u00f5es personalizadas para cada objetivo.',
        },
        {
          title: 'Monitoramento ativo',
          description: 'Receba alertas sempre que for hora de aportar ou rebalancear.',
        },
        {
          title: 'Insights inteligentes',
          description:
            'Descubra exatamente o que fazer para manter o plano no rumo certo.',
        },
      ],
      heroTitle: 'Comece a investir',
      heroEmphasis: 'com clareza e controle',
      subtitle:
        'Em poucos minutos voc\u00ea configura objetivos, define aportes e recebe recomenda\u00e7\u00f5es personalizadas.',
      panelAriaLabel: 'Crie sua conta',
      formTitle: 'Crie sua conta',
      formSubtitle: 'Informe seus dados para testar o Touzi.',
      fullNameLabel: 'Nome completo',
      fullNamePlaceholder: 'Nome e sobrenome',
      emailLabel: 'E-mail',
      emailPlaceholder: 'voce@email.com',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Crie uma senha segura',
      confirmPasswordLabel: 'Confirme a senha',
      confirmPasswordPlaceholder: 'Repita a senha',
      successMessage:
        'Conta criada com sucesso! Verifique seu e-mail para confirmar o acesso.',
      submitLabel: 'Criar conta',
      submitLoading: 'Criando conta...',
      switchPrompt: 'J\u00e1 possui cadastro?',
      switchAction: 'Acesse sua conta',
    },
    settings: {
      eyebrow: 'Configura\u00e7\u00f5es',
      title: 'Dados da conta',
      subtitle:
        'Seu nome pode ser atualizado. O e-mail permanece bloqueado por seguran\u00e7a.',
      identificationTitle: 'Identifica\u00e7\u00e3o',
      identificationSubtitle:
        'Essas informa\u00e7\u00f5es aparecem em relat\u00f3rios e comunicados.',
      emailBadge: 'E-mail bloqueado',
      nameLabel: 'Nome',
      namePlaceholder: 'Digite seu nome',
      nameHelper: 'Voc\u00ea pode ajustar sempre que necess\u00e1rio.',
      emailLabel: 'E-mail',
      emailPlaceholder: 'E-mail n\u00e3o informado',
      emailHelper: 'Este e-mail \u00e9 usado para login.',
      saveName: 'Salvar nome',
      savingName: 'Salvando...',
      nameUpdated: 'Nome atualizado com sucesso.',
      nameSaveError: 'Erro ao salvar nome.',
      preferencesTitle: 'Prefer\u00eancias',
      preferencesSubtitle: 'Personalize idioma e formatos.',
      languageLabel: 'Idioma',
      languageHelper: 'A interface ser\u00e1 atualizada imediatamente.',
      languageOptionPt: 'Portugu\u00eas (Brasil)',
      languageOptionEn: 'Ingl\u00eas',
      languageSaved: 'Idioma atualizado com sucesso.',
      languageSaveError: 'Erro ao salvar idioma.',
      themeLabel: 'Tema',
      themeHelper: 'A interface será atualizada imediatamente.',
      themeLight: 'Claro',
      themeDark: 'Escuro',
    },
    dashboard: {
      error: 'Erro ao carregar carteira',
      loading: 'Carregando carteira...',
      empty: 'Nenhuma aloca\u00e7\u00e3o encontrada.',
    },
    dashboardModern: {
      error: 'Erro ao carregar carteira',
      hero: {
        eyebrow: 'Vis\u00e3o geral',
        title: 'Sa\u00fade da carteira',
        subtitle:
          'Patrim\u00f4nio consolidado com comparativo das metas por classe de ativo.',
        label: 'Patrim\u00f4nio total',
        allocationSingle: '1 classe configurada',
        allocationPlural: 'classes configuradas',
        reachedSingle: '1 acima da meta',
        reachedPlural: 'acima da meta',
        consolidating: 'Consolidando...',
        consolidate: 'Consolidar carteira',
        consolidateTooltip:
          'Carteira precisa ser consolidada para refletir os dados mais recentes.',
        emptyHint: 'Nenhuma classe configurada.',
        configureClasses: 'Configurar classes',
      },
      loading: 'Carregando carteira...',
      setup: {
        title: 'Comece configurando sua carteira',
        subtitle:
          'Defina as classes, escolha os ativos e simule aportes para ver a sa\u00fade da carteira.',
        steps: [
          {
            title: 'Carteira meta',
            description: 'Distribua os percentuais alvo por classe.',
            action: 'Configurar',
          },
          {
            title: 'Ativos',
            description: 'Adicione tickers e notas de prioridade.',
            action: 'Definir',
          },
          {
            title: 'Aportes',
            description: 'Simule um aporte para receber recomenda\u00e7\u00f5es.',
            action: 'Simular',
          },
        ],
      },
      allocationMap: {
        label: 'Distribui\u00e7\u00e3o por classe',
        title: 'Mapa de aloca\u00e7\u00e3o',
        goal: 'Meta total',
        total: 'Total',
      },
      progress: {
        label: 'Progresso por classe',
        title: 'Meta e realizado',
        goalPrefix: 'Meta',
        currentPrefix: 'Atual',
        above: 'Acima da meta',
        below: 'Abaixo da meta',
      },
      distribution: {
        label: 'Distribu\u00eddo por m\u00eas',
        title: 'Hist\u00f3rico de distribui\u00e7\u00e3o',
        loading: 'Carregando...',
        emptyTitle: 'Sem aportes registrados',
        emptyText: 'Simule um aporte para alimentar o hist\u00f3rico.',
        simulate: 'Simular aporte',
      },
    },
    dashboardStudio: {
      error: 'Erro ao carregar dashboard',
      loading: 'Carregando dashboard...',
      toolbar: {
        adjustClasses: 'Ajustar classes',
        simulateContribution: 'Simular aporte',
        refreshing: 'Atualizando...',
      },
      consolidation: {
        title: 'Carteira desatualizada',
        text: 'Consolide para refletir as \u00faltimas quantidades.',
        action: 'Consolidar agora',
        loading: 'Consolidando...',
      },
      hero: {
        label: 'Patrim\u00f4nio total',
        goal: 'Meta total',
        deltaToGoal: 'Falta para a meta',
        deltaAbove: 'Acima da meta',
        deltaTotal: 'Meta total',
        pace: 'Ritmo 30d',
        above: 'Classes acima',
        lastContribution: '\u00daltimo aporte',
        lastContributionEmpty: 'Sem aportes',
      },
      summaryCards: {
        totalAssets: 'Patrim\u00f4nio Total',
        totalInvested: 'Total Investido',
        profitLoss: 'Lucro / Preju\u00edzo',
        profitability: 'Rentabilidade',
        estimatedWarning: (tickers: string) =>
          `PM n\u00e3o informado para ${tickers}. Usando cota\u00e7\u00e3o atual como estimativa.`,
      },
      allocationChart: {
        title: 'Aloca\u00e7\u00e3o por Tipo',
      },
      topAssets: {
        title: 'Principais Ativos',
        empty: 'Nenhum ativo encontrado.',
      },
      goalsComparison: {
        title: 'Metas vs. Aloca\u00e7\u00e3o Atual',
        empty: 'Nenhuma classe configurada.',
        current: 'Atual',
        goal: 'Meta',
      },
      goalsPanel: {
        kicker: 'Metas',
        title: 'Classe por classe',
        classSingle: 'classe',
        classPlural: 'classes',
        metaPrefix: 'Meta',
        statusAbove: 'Acima',
        statusBelow: 'Abaixo',
        empty: 'Nenhuma classe configurada.',
      },
      focusPanel: {
        kicker: 'foco',
        title: 'Maiores defasagens',
        belowLabel: 'abaixo',
        gapPrefix: 'Falta',
        empty: 'Todas as classes est\u00e3o na meta.',
      },
      pulsePanel: {
        kicker: 'aportes',
        title: '\u00daltimos movimentos',
        empty: 'Sem aportes recentes.',
      },
      historyPanel: {
        kicker: 'Hist\u00f3rico curto',
        title: 'Aportes recentes',
        recordSingle: 'registro',
        recordPlural: 'registros',
        metaSingle: 'classe',
        metaPlural: 'classes',
        metaFallback: 'Aporte',
        empty: 'Nenhum aporte registrado.',
      },
    },
    contributions: {
      errors: {
        assetClasses: 'Erro ao carregar classes de ativos',
        assets: 'Erro ao carregar ativos',
        contributions: 'Erro ao calcular aporte',
        save: 'Erro ao salvar aporte',
        consolidate: 'Erro ao consolidar carteira',
        invalid: 'Simule um aporte v\u00e1lido antes de salvar.',
      },
      saveSuccess: 'Aporte salvo com sucesso. Consolidando carteira...',
      classFallback: 'Classe',
      header: {
        eyebrow: 'Aportes',
        title: 'Simule um novo aporte',
        subtitle:
          'Defina o valor do aporte e veja quantos ativos comprar para seguir a carteira meta.',
        distributed: 'Valor distribu\u00eddo',
        remainder: 'Valor remanescente',
      },
      amount: {
        label: 'Valor do aporte',
        placeholder: '0,00',
        error: 'Informe um valor maior que zero.',
        hint:
          'Distribu\u00edmos o aporte pelos percentuais das classes e usamos a nota do ativo para priorizar a compra.',
      },
      table: {
        title: 'Recomenda\u00e7\u00e3o de compras',
        subtitle: 'Veja quais ativos comprar com este aporte e a quantidade estimada.',
        columns: {
          asset: 'Ativo',
          class: 'Classe',
          rating: 'Nota',
          price: 'Pre\u00e7o',
          quantity: 'Quantidade a comprar',
          value: 'Valor do aporte no ativo',
        },
        total: 'Total sugerido',
        remainder: 'Remanescente',
      },
      states: {
        loadingPortfolio: 'Carregando dados da carteira...',
        missingAmount: 'Informe um valor de aporte para calcular as compras.',
        loadingRecommendations: 'Carregando recomenda\u00e7\u00f5es...',
        noClasses: 'Nenhuma classe configurada.',
        noAssets: 'Nenhum ativo cadastrado na carteira meta.',
        noSuggestions: 'Nada para sugerir com a configura\u00e7\u00e3o atual.',
      },
      saveButton: {
        save: 'Salvar aporte',
        saving: 'Salvando...',
        consolidating: 'Consolidando...',
      },
      disabledReason: {
        noClasses: 'Configure as classes de ativos.',
        noAssets: 'Cadastre ativos na carteira meta.',
        reviewErrors: 'Revise os erros para continuar.',
        loading: 'Carregando dados...',
        noRecommendations: 'Sem recomenda\u00e7\u00f5es para este valor.',
      },
      flow: {
        amount: 'Valor do aporte informado',
        classes: 'Classes de ativos configuradas',
        assets: 'Carteira meta com ativos',
        recommendations: 'Recomenda\u00e7\u00f5es geradas',
        done: 'Conclu\u00eddo',
        pending: 'Aguardando',
        loading: 'Carregando',
        ready: 'Tudo pronto para salvar o aporte.',
      },
    },
    assetClasses: {
      title: 'Carteira meta',
      subtitle: 'Escolha as classes suportadas e defina os percentuais',
      errors: {
        add: 'Erro ao adicionar classe',
        update: 'Erro ao salvar classe',
        remove: 'Erro ao remover classe',
        load: 'Erro ao carregar classes de ativos',
      },
      summary: {
        label: 'Soma dos percentuais',
        hint: 'Ajuste os percentuais at\u00e9 fechar 100%.',
      },
      loading: 'Carregando classes...',
      empty: 'Nenhuma classe configurada.',
      add: 'Adicionar classe',
      dialog: {
        title: 'Adicionar classe',
        description:
          'Selecione uma classe suportada para incluir na distribui\u00e7\u00e3o. Classes j\u00e1 usadas ficam indispon\u00edveis.',
        close: 'Fechar modal',
        tooltipLabel: 'Descri\u00e7\u00e3o da classe',
        added: 'Adicionado',
      },
      remove: 'Remover',
      preview: {
        ariaLabel: 'Pr\u00e9via de distribui\u00e7\u00e3o',
        title: 'Pr\u00e9via',
        remaining: 'Restante',
      },
      percentageOfTotal: '% do total',
      sliderAriaPrefix: 'Definir percentual para',
      valueAriaPrefix: 'Valor percentual de',
    },
    targetPortfolio: {
      header: {
        eyebrow: 'Ativos',
        title: 'Meus ativos',
        subtitle:
          'Visualize todos os seus ativos, acompanhe pre\u00e7o m\u00e9dio, valor atual e rentabilidade.',
      },
      viewMode: {
        cards: 'Cards',
        grouped: 'Agrupado',
      },
      addAsset: {
        button: 'Adicionar ativo',
        dialogTitle: 'Adicionar novo ativo',
        dialogDescription: 'Preencha os dados do ativo para adicion\u00e1-lo \u00e0 sua carteira.',
        classLabel: 'Classe do ativo',
        classPlaceholder: 'Selecione a classe',
        tickerLabel: 'Ticker',
        tickerPlaceholder: 'ex: VALE3',
        quantityLabel: 'Quantidade',
        ratingLabel: 'Nota (0-10)',
        ratingHint: 'Prioridade na distribui\u00e7\u00e3o de aportes',
        averagePriceLabel: 'Pre\u00e7o m\u00e9dio',
        averagePricePlaceholder: '0,00',
        submit: 'Adicionar',
        submitting: 'Adicionando...',
        cancel: 'Cancelar',
      },
      editAsset: {
        dialogTitle: 'Editar ativo',
        dialogDescription: 'Altere as informa\u00e7\u00f5es do ativo.',
        tickerLabel: 'Ticker',
        quantityLabel: 'Quantidade',
        averagePriceLabel: 'Pre\u00e7o m\u00e9dio',
        averagePricePlaceholder: '0,00',
        ratingLabel: 'Nota (0-10)',
        ratingHint: 'Prioridade na distribui\u00e7\u00e3o de aportes',
        submit: 'Salvar',
        submitting: 'Salvando...',
        cancel: 'Cancelar',
      },
      card: {
        quantity: 'Qtd.',
        averagePrice: 'PM',
        currentPrice: 'Atual',
        total: 'Total',
        priceUnavailable: '--',
        estimatedTotal: 'Estimado pela cotação atual',
      },
      errors: {
        add: 'Erro ao adicionar ativo',
        update: 'Erro ao atualizar ativo',
        remove: 'Erro ao remover ativo',
        loadClasses: 'Erro ao carregar classes de ativos',
        loadAssets: 'Erro ao carregar ativos',
        consolidate:
          'N\u00e3o foi poss\u00edvel marcar consolida\u00e7\u00e3o da carteira',
      },
      empty: {
        title: 'Nenhum ativo cadastrado',
        description: 'Adicione ativos \u00e0 sua carteira para come\u00e7ar a acompanhar seu patrim\u00f4nio.',
        configure: 'Configurar classes',
      },
      loading: 'Carregando ativos...',
      fallbackDescription: 'Classe configurada.',
    },
    market: {
      header: {
        eyebrow: 'Mercado',
        title: 'Mercado',
        subtitle: 'Encontre tickers e acompanhe pre\u00e7o e varia\u00e7\u00e3o.',
      },
      search: {
        label: 'Buscar ticker ou nome',
        placeholder: 'Ex: VALE3, HGLG11, BTC...',
        clear: 'Limpar busca',
      },
      filters: {
        classLabel: 'Classe de ativo',
        classAll: 'Todas as classes',
      },
      list: {
        results: 'Resultados',
        resultsEmpty: 'Nenhum ativo encontrado.',
        price: 'Pre\u00e7o',
        change: 'Varia\u00e7\u00e3o',
        inWallet: 'Na carteira',
        loading: 'Carregando ativos...',
        loadingMore: 'Carregando mais...',
        loadMore: 'Carregar mais',
        error: 'Erro ao carregar ativos',
      },
      details: {
        title: 'Mercado',
        subtitle: 'Detalhes do ativo selecionado.',
        back: 'Voltar para busca',
        backToMarket: 'Voltar para Mercado',
        notFound: 'Ativo n\u00e3o encontrado.',
        website: 'Site oficial',
        variationPercent: 'Varia\u00e7\u00e3o (%)',
        variationAbsolute: 'Varia\u00e7\u00e3o absoluta',
        actions: {
          simulate: 'Simular compra',
          addWallet: 'Adicionar \u00e0 carteira',
        },
        fundamentals: {
          title: 'Indicadores fundamentalistas',
          subtitle: '\u00daltimos 12m (exemplo)',
          empty: 'Indicadores indispon\u00edveis para este ativo.',
          comingSoon: 'Em breve',
          labels: {
            pl: 'P/L',
            pvp: 'P/VP',
            dy: 'Dividend Yield',
            roe: 'ROE',
            netMargin: 'Margem l\u00edquida',
            debtEbitda: 'D\u00edvida/EBITDA',
          },
        },
        auxiliary: {
          title: 'Dados auxiliares',
          earningsPerShare: 'LPA (Lucro por a\u00e7\u00e3o)',
          marketCap: 'Market Cap',
        },
        chart: {
          title: 'Evolu\u00e7\u00e3o (placeholder)',
          description:
            'Aqui voc\u00ea pode plugar um gr\u00e1fico simples (pre\u00e7o 12m, DY 12m, etc.).',
        },
        quickActions: {
          title: 'A\u00e7\u00f5es r\u00e1pidas',
          simulate: 'Simular compra deste ativo',
          compare: 'Ver ativos similares',
        },
        position: {
          title: 'Posi\u00e7\u00e3o (exemplo)',
          quantity: 'Quantidade',
          averagePrice: 'Pre\u00e7o m\u00e9dio',
          invested: 'Valor investido',
          empty: 'Sem posi\u00e7\u00e3o cadastrada para este ativo.',
        },
      },
    },
    wip: {
      title: 'Em Desenvolvimento',
      description:
        'Esta p\u00e1gina est\u00e1 em desenvolvimento e estar\u00e1 dispon\u00edvel em breve.',
    },
    assetClassesCatalog: {
      [AssetClassCode.ACAO]: {
        name: 'A\u00e7\u00f5es',
        description:
          'Empresas listadas em bolsa com maior volatilidade e potencial de crescimento.',
      },
      [AssetClassCode.FII]: {
        name: 'FIIs',
        description:
          'Fundos Imobili\u00e1rios focados em renda recorrente e prote\u00e7\u00e3o inflacion\u00e1ria.',
      },
      [AssetClassCode.BDRS]: {
        name: 'BDRs',
        description:
          'Recibos que representam a\u00e7\u00f5es de empresas estrangeiras negociadas no Brasil.',
      },
    },
  },
  en: {
    common: {
      currencySymbol: 'R$',
    },
    header: {
      accountFallback: 'Account',
      emailFallback: 'Email not provided',
      profileLabel: 'User profile',
      settingsLabel: 'Settings',
      signOutLabel: 'Sign out',
    },
    sidebar: {
      navAriaLabel: 'Main navigation',
      dashboard: 'Dashboard',
      contributions: 'Contributions',
      assetClasses: 'Target portfolio',
      targetPortfolio: 'Assets',
      market: 'Market',
      proventos: 'Dividends',
      settings: 'Settings',
      unlock: 'Hide sidebar',
      lock: 'Open sidebar',
      lockedTitle: 'Sidebar open',
      floatingTitle: 'Sidebar closed',
    },
    authGuard: {
      validating: 'Validating session...',
    },
    allocationCard: {
      title: 'Current allocation',
      subtitle: 'Indicator with goal marker',
    },
    allocationProgress: {
      above: 'Above goal',
      below: 'Below goal',
    },
    totalAssetsCard: {
      title: 'Total assets',
    },
    login: {
      validations: {
        emailRequired: 'Enter a valid email',
        emailInvalid: 'Invalid email format',
        passwordRequired: 'Enter your password',
        passwordMin: 'Password must be at least 8 characters',
      },
      highlights: [
        {
          title: 'Contextual rebalancing',
          description: 'Prioritize contributions and sells based on your targets.',
        },
        {
          title: 'Smart alerts',
          description: 'Get notified whenever an asset leaves the ideal band.',
        },
        {
          title: 'Actionable insights',
          description: 'Clear next steps to keep your plan on track.',
        },
      ],
      heroTitle: 'Total control of your portfolio',
      heroEmphasis: 'in one place',
      subtitle:
        'Centralize contributions, track targets, and rebalance with recommendations made for you.',
      panelAriaLabel: 'System access',
      formTitle: 'Sign in to your account',
      formSubtitle: 'Use your registered email to access the platform.',
      emailLabel: 'Email',
      emailPlaceholder: 'you@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      submitLabel: 'Sign in',
      submitLoading: 'Signing in...',
      switchPrompt: "Don't have an account?",
      switchAction: 'Create one',
    },
    signup: {
      validations: {
        fullNameRequired: 'Enter your full name',
        emailRequired: 'Enter a valid email',
        emailInvalid: 'Invalid email format',
        passwordRequired: 'Create a password',
        passwordMin: 'Password must be at least 8 characters',
        confirmPasswordRequired: 'Confirm your password',
        confirmPasswordMatch: 'Passwords must match',
      },
      highlights: [
        {
          title: 'Guided planning',
          description: 'Build your portfolio with personalized suggestions.',
        },
        {
          title: 'Active monitoring',
          description: 'Get alerts whenever it is time to contribute or rebalance.',
        },
        {
          title: 'Smart insights',
          description: 'Know exactly what to do to stay on track.',
        },
      ],
      heroTitle: 'Start investing',
      heroEmphasis: 'with clarity and control',
      subtitle:
        'In minutes you set goals, define contributions, and receive tailored recommendations.',
      panelAriaLabel: 'Create your account',
      formTitle: 'Create your account',
      formSubtitle: 'Share your details to try Touzi.',
      fullNameLabel: 'Full name',
      fullNamePlaceholder: 'First and last name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Create a strong password',
      confirmPasswordLabel: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat your password',
      successMessage: 'Account created! Check your email to confirm access.',
      submitLabel: 'Create account',
      submitLoading: 'Creating account...',
      switchPrompt: 'Already have an account?',
      switchAction: 'Sign in',
    },
    settings: {
      eyebrow: 'Settings',
      title: 'Account details',
      subtitle: 'Your name can be updated. Email remains locked for security.',
      identificationTitle: 'Identification',
      identificationSubtitle: 'These details appear in reports and communications.',
      emailBadge: 'Email locked',
      nameLabel: 'Name',
      namePlaceholder: 'Enter your name',
      nameHelper: 'You can adjust it whenever needed.',
      emailLabel: 'Email',
      emailPlaceholder: 'Email not provided',
      emailHelper: 'This email is used for login.',
      saveName: 'Save name',
      savingName: 'Saving...',
      nameUpdated: 'Name updated successfully.',
      nameSaveError: 'Failed to save name.',
      preferencesTitle: 'Preferences',
      preferencesSubtitle: 'Personalize language and formats.',
      languageLabel: 'Language',
      languageHelper: 'The interface updates immediately.',
      languageOptionPt: 'Portuguese (Brazil)',
      languageOptionEn: 'English',
      languageSaved: 'Language updated successfully.',
      languageSaveError: 'Failed to save language.',
      themeLabel: 'Theme',
      themeHelper: 'The interface updates immediately.',
      themeLight: 'Light',
      themeDark: 'Dark',
    },
    dashboard: {
      error: 'Failed to load portfolio',
      loading: 'Loading portfolio...',
      empty: 'No allocations found.',
    },
    dashboardModern: {
      error: 'Failed to load portfolio',
      hero: {
        eyebrow: 'Overview',
        title: 'Portfolio health',
        subtitle: 'Consolidated holdings with target comparison by asset class.',
        label: 'Total assets',
        allocationSingle: '1 class configured',
        allocationPlural: 'classes configured',
        reachedSingle: '1 above target',
        reachedPlural: 'above target',
        consolidating: 'Consolidating...',
        consolidate: 'Consolidate portfolio',
        consolidateTooltip:
          'Portfolio needs consolidation to reflect the latest data.',
        emptyHint: 'No classes configured.',
        configureClasses: 'Configure classes',
      },
      loading: 'Loading portfolio...',
      setup: {
        title: 'Start by setting up your portfolio',
        subtitle:
          'Define asset classes, choose assets, and simulate contributions to see portfolio health.',
        steps: [
          {
            title: 'Target portfolio',
            description: 'Distribute target percentages by class.',
            action: 'Configure',
          },
          {
            title: 'Assets',
            description: 'Add tickers and priority ratings.',
            action: 'Define',
          },
          {
            title: 'Contributions',
            description: 'Simulate a contribution to get recommendations.',
            action: 'Simulate',
          },
        ],
      },
      allocationMap: {
        label: 'Allocation by class',
        title: 'Allocation map',
        goal: 'Total target',
        total: 'Total',
      },
      progress: {
        label: 'Progress by class',
        title: 'Target vs actual',
        goalPrefix: 'Target',
        currentPrefix: 'Current',
        above: 'Above target',
        below: 'Below target',
      },
      distribution: {
        label: 'Distributed by month',
        title: 'Distribution history',
        loading: 'Loading...',
        emptyTitle: 'No contributions recorded',
        emptyText: 'Simulate a contribution to feed the history.',
        simulate: 'Simulate contribution',
      },
    },
    dashboardStudio: {
      error: 'Failed to load dashboard',
      loading: 'Loading dashboard...',
      toolbar: {
        adjustClasses: 'Adjust classes',
        simulateContribution: 'Simulate contribution',
        refreshing: 'Refreshing...',
      },
      consolidation: {
        title: 'Portfolio out of date',
        text: 'Consolidate to reflect the latest quantities.',
        action: 'Consolidate now',
        loading: 'Consolidating...',
      },
      hero: {
        label: 'Total assets',
        goal: 'Total target',
        deltaToGoal: 'To target',
        deltaAbove: 'Above target',
        deltaTotal: 'Total target',
        pace: '30d pace',
        above: 'Classes above',
        lastContribution: 'Last contribution',
        lastContributionEmpty: 'No contributions',
      },
      summaryCards: {
        totalAssets: 'Total Assets',
        totalInvested: 'Total Invested',
        profitLoss: 'Profit / Loss',
        profitability: 'Profitability',
        estimatedWarning: (tickers: string) =>
          `Avg. price not set for ${tickers}. Using current price as estimate.`,
      },
      allocationChart: {
        title: 'Allocation by Type',
      },
      topAssets: {
        title: 'Top Assets',
        empty: 'No assets found.',
      },
      goalsComparison: {
        title: 'Goals vs. Current Allocation',
        empty: 'No classes configured.',
        current: 'Current',
        goal: 'Goal',
      },
      goalsPanel: {
        kicker: 'Goals',
        title: 'Class by class',
        classSingle: 'class',
        classPlural: 'classes',
        metaPrefix: 'Target',
        statusAbove: 'Above',
        statusBelow: 'Below',
        empty: 'No classes configured.',
      },
      focusPanel: {
        kicker: 'focus',
        title: 'Largest gaps',
        belowLabel: 'below',
        gapPrefix: 'Short',
        empty: 'All classes are on target.',
      },
      pulsePanel: {
        kicker: 'contributions',
        title: 'Latest moves',
        empty: 'No recent contributions.',
      },
      historyPanel: {
        kicker: 'Short history',
        title: 'Recent contributions',
        recordSingle: 'record',
        recordPlural: 'records',
        metaSingle: 'class',
        metaPlural: 'classes',
        metaFallback: 'Contribution',
        empty: 'No contributions recorded.',
      },
    },
    contributions: {
      errors: {
        assetClasses: 'Failed to load asset classes',
        assets: 'Failed to load assets',
        contributions: 'Failed to calculate contribution',
        save: 'Failed to save contribution',
        consolidate: 'Failed to consolidate portfolio',
        invalid: 'Simulate a valid contribution before saving.',
      },
      saveSuccess: 'Contribution saved. Consolidating portfolio...',
      classFallback: 'Class',
      header: {
        eyebrow: 'Contributions',
        title: 'Simulate a new contribution',
        subtitle:
          'Set the contribution value and see which assets to buy to follow the target portfolio.',
        distributed: 'Distributed value',
        remainder: 'Remaining value',
      },
      amount: {
        label: 'Contribution value',
        placeholder: '0.00',
        error: 'Enter a value greater than zero.',
        hint:
          'We distribute the contribution by class targets and prioritize purchases by asset rating.',
      },
      table: {
        title: 'Purchase recommendations',
        subtitle: 'See which assets to buy with this contribution and the estimated quantity.',
        columns: {
          asset: 'Asset',
          class: 'Class',
          rating: 'Rating',
          price: 'Price',
          quantity: 'Quantity to buy',
          value: 'Contribution value by asset',
        },
        total: 'Total suggested',
        remainder: 'Remainder',
      },
      states: {
        loadingPortfolio: 'Loading portfolio data...',
        missingAmount: 'Enter a contribution value to calculate purchases.',
        loadingRecommendations: 'Loading recommendations...',
        noClasses: 'No classes configured.',
        noAssets: 'No assets registered in target portfolio.',
        noSuggestions: 'Nothing to suggest with the current setup.',
      },
      saveButton: {
        save: 'Save contribution',
        saving: 'Saving...',
        consolidating: 'Consolidating...',
      },
      disabledReason: {
        noClasses: 'Configure asset classes.',
        noAssets: 'Add assets to the target portfolio.',
        reviewErrors: 'Review the errors to continue.',
        loading: 'Loading data...',
        noRecommendations: 'No recommendations for this value.',
      },
      flow: {
        amount: 'Contribution amount set',
        classes: 'Asset classes configured',
        assets: 'Target portfolio with assets',
        recommendations: 'Recommendations generated',
        done: 'Completed',
        pending: 'Waiting',
        loading: 'Loading',
        ready: 'Ready to save the contribution.',
      },
    },
    assetClasses: {
      title: 'Target portfolio',
      subtitle: 'Choose supported classes and define target percentages',
      errors: {
        add: 'Failed to add class',
        update: 'Failed to save class',
        remove: 'Failed to remove class',
        load: 'Failed to load asset classes',
      },
      summary: {
        label: 'Total percentage',
        hint: 'Adjust percentages until reaching 100%.',
      },
      loading: 'Loading classes...',
      empty: 'No classes configured.',
      add: 'Add class',
      dialog: {
        title: 'Add class',
        description:
          'Select a supported class to include in the allocation. Classes already used are unavailable.',
        close: 'Close dialog',
        tooltipLabel: 'Class description',
        added: 'Added',
      },
      remove: 'Remove',
      preview: {
        ariaLabel: 'Allocation preview',
        title: 'Preview',
        remaining: 'Remaining',
      },
      percentageOfTotal: '% of total',
      sliderAriaPrefix: 'Set percentage for',
      valueAriaPrefix: 'Percentage value for',
    },
    targetPortfolio: {
      header: {
        eyebrow: 'Assets',
        title: 'My assets',
        subtitle:
          'View all your assets, track average price, current value and returns.',
      },
      viewMode: {
        cards: 'Cards',
        grouped: 'Grouped',
      },
      addAsset: {
        button: 'Add asset',
        dialogTitle: 'Add new asset',
        dialogDescription: 'Fill in the asset details to add it to your portfolio.',
        classLabel: 'Asset class',
        classPlaceholder: 'Select class',
        tickerLabel: 'Ticker',
        tickerPlaceholder: 'e.g. VALE3',
        quantityLabel: 'Quantity',
        ratingLabel: 'Rating (0-10)',
        ratingHint: 'Priority in contribution distribution',
        averagePriceLabel: 'Average price',
        averagePricePlaceholder: '0.00',
        submit: 'Add',
        submitting: 'Adding...',
        cancel: 'Cancel',
      },
      editAsset: {
        dialogTitle: 'Edit asset',
        dialogDescription: 'Update the asset information.',
        tickerLabel: 'Ticker',
        quantityLabel: 'Quantity',
        averagePriceLabel: 'Average price',
        averagePricePlaceholder: '0.00',
        ratingLabel: 'Rating (0-10)',
        ratingHint: 'Priority in contribution distribution',
        submit: 'Save',
        submitting: 'Saving...',
        cancel: 'Cancel',
      },
      card: {
        quantity: 'Qty.',
        averagePrice: 'Avg.',
        currentPrice: 'Current',
        total: 'Total',
        priceUnavailable: '--',
        estimatedTotal: 'Estimated by current price',
      },
      errors: {
        add: 'Failed to add asset',
        update: 'Failed to update asset',
        remove: 'Failed to remove asset',
        loadClasses: 'Failed to load asset classes',
        loadAssets: 'Failed to load assets',
        consolidate: 'Could not mark portfolio for consolidation',
      },
      empty: {
        title: 'No assets registered',
        description: 'Add assets to your portfolio to start tracking your holdings.',
        configure: 'Configure classes',
      },
      loading: 'Loading assets...',
      fallbackDescription: 'Configured class.',
    },
    market: {
      header: {
        eyebrow: 'Market',
        title: 'Search market assets',
        subtitle: 'Find tickers and track price changes.',
      },
      search: {
        label: 'Search ticker or name',
        placeholder: 'Ex: VALE3, HGLG11, BTC...',
        clear: 'Clear search',
      },
      filters: {
        classLabel: 'Asset class',
        classAll: 'All classes',
      },
      list: {
        results: 'Results',
        resultsEmpty: 'No assets found.',
        price: 'Price',
        change: 'Change',
        inWallet: 'In portfolio',
        loading: 'Loading assets...',
        loadingMore: 'Loading more...',
        loadMore: 'Load more',
        error: 'Failed to load assets',
      },
      details: {
        title: 'Market',
        subtitle: 'Selected asset details.',
        back: 'Back to search',
        backToMarket: 'Back to Market',
        notFound: 'Asset not found.',
        website: 'Official website',
        variationPercent: 'Change (%)',
        variationAbsolute: 'Absolute change',
        actions: {
          simulate: 'Simulate buy',
          addWallet: 'Add to portfolio',
        },
        fundamentals: {
          title: 'Fundamentals',
          subtitle: 'Last 12m (example)',
          empty: 'Fundamentals unavailable for this asset.',
          comingSoon: 'Coming soon',
          labels: {
            pl: 'P/E',
            pvp: 'P/B',
            dy: 'Dividend yield',
            roe: 'ROE',
            netMargin: 'Net margin',
            debtEbitda: 'Debt/EBITDA',
          },
        },
        auxiliary: {
          title: 'Additional data',
          earningsPerShare: 'EPS',
          marketCap: 'Market cap',
        },
        chart: {
          title: 'Performance (placeholder)',
          description: 'Plug a simple chart here (12m price, DY, etc.).',
        },
        quickActions: {
          title: 'Quick actions',
          simulate: 'Simulate buying this asset',
          compare: 'See similar assets',
        },
        position: {
          title: 'Position (example)',
          quantity: 'Quantity',
          averagePrice: 'Average price',
          invested: 'Invested value',
          empty: 'No position registered for this asset.',
        },
      },
    },
    wip: {
      title: 'In Development',
      description: 'This page is under development and will be available soon.',
    },
    assetClassesCatalog: {
      [AssetClassCode.ACAO]: {
        name: 'Stocks',
        description:
          'Public companies with higher volatility and growth potential.',
      },
      [AssetClassCode.FII]: {
        name: 'FIIs',
        description:
          'Brazilian real estate funds focused on recurring income and inflation protection.',
      },
      [AssetClassCode.BDRS]: {
        name: 'BDRs',
        description:
          'Receipts that represent shares of foreign companies traded in Brazil.',
      },
    },
  },
} as const;
